import { z } from 'zod';
import { evidence, isoDate, uuid } from './common';

/**
 * Knowledge (PRD 30): what the company knows, kept current by its meetings.
 * Three kinds of statement, a small state machine, and a source that is a
 * STRETCH of a meeting — never a moment.
 */
export const knowledgeKind = z.enum(['decision', 'term', 'fact']);
export type KnowledgeKind = z.infer<typeof knowledgeKind>;

export const knowledgeStatus = z.enum([
  'seen',
  'proposed',
  'current',
  'superseded',
  'retired',
  'declined',
]);
export type KnowledgeStatus = z.infer<typeof knowledgeStatus>;

export const knowledgeSubject = z.object({
  type: z.enum(['us', 'company', 'person', 'series']),
  /** A company's domain, a person's email, a series key; null for "us" or when unresolved. */
  key: z.string().nullable(),
  name: z.string().nullable(),
});
export type KnowledgeSubject = z.infer<typeof knowledgeSubject>;

export const knowledgeMeetingRef = z.object({
  meetingId: uuid,
  title: z.string(),
  startedAt: isoDate,
  seriesKey: z.string().nullable(),
});

const chainLink = z.object({ id: uuid, statement: z.string() });

export const knowledgeEntry = z.object({
  id: uuid,
  kind: knowledgeKind,
  statement: z.string(),
  detail: z.string().nullable(),
  subject: knowledgeSubject.nullable(),
  status: knowledgeStatus,
  /** Current, but nothing has mentioned or confirmed it for ~90 days. */
  quiet: z.boolean(),
  grade: evidence,
  sightings: z.number().int(),
  firstSeenAt: isoDate,
  lastMentionedAt: isoDate,
  confirmedAt: isoDate.nullable(),
  declinedAt: isoDate.nullable(),
  supersedes: chainLink.nullable(),
  supersededBy: chainLink.nullable(),
  lastSource: knowledgeMeetingRef.nullable(),
  sourceCount: z.number().int(),
  /** Whether THIS reader may keep, decline, retire or edit it: they are in its space. */
  canAct: z.boolean(),
});
export type KnowledgeEntry = z.infer<typeof knowledgeEntry>;

export const knowledgeOverview = z.object({
  counts: z.object({
    statements: z.number().int(),
    proposed: z.number().int(),
    quiet: z.number().int(),
    /** Heard once, not yet proposed. Shown as a count on day one, never as rows. */
    seen: z.number().int(),
  }),
  proposed: z.array(knowledgeEntry),
  current: z.array(knowledgeEntry),
  quiet: z.array(knowledgeEntry),
  /** The backfill: meetings with a transcript this reader can open, and how many the knowledge pass has not read yet. */
  reading: z.object({ unread: z.number().int(), total: z.number().int() }),
  /** Three names, one thing: current entries of one kind that say the same thing, the likeliest winner first. */
  merges: z.array(z.object({ entries: z.array(knowledgeEntry) })),
});
export type KnowledgeOverview = z.infer<typeof knowledgeOverview>;

export const knowledgeLine = z.object({
  id: uuid,
  speakerName: z.string().nullable(),
  text: z.string(),
  spokeAt: isoDate.nullable(),
});

export const knowledgeSource = z.object({
  id: uuid,
  role: z.enum(['origin', 'mention', 'contradiction', 'confirmation']),
  meetingId: uuid,
  title: z.string(),
  startedAt: isoDate,
  startSegmentId: uuid.nullable(),
  endSegmentId: uuid.nullable(),
  startMs: z.number().int().nullable(),
  endMs: z.number().int().nullable(),
  speakerName: z.string().nullable(),
  quote: z.string().nullable(),
  /** The lines behind it — only when this reader may open the meeting; else empty, and `canOpen` says why. */
  lines: z.array(knowledgeLine),
  canOpen: z.boolean(),
});
export type KnowledgeSource = z.infer<typeof knowledgeSource>;

export const knowledgeDetail = knowledgeEntry.extend({
  sources: z.array(knowledgeSource),
  alternatives: z.array(z.object({ option: z.string(), whyNot: z.string().nullable() })),
  /** Oldest first, this entry included: how the thinking changed. */
  chain: z.array(
    z.object({
      id: uuid,
      statement: z.string(),
      status: knowledgeStatus,
      firstSeenAt: isoDate,
      confirmedAt: isoDate.nullable(),
      sightings: z.number().int(),
    }),
  ),
  events: z.array(
    z.object({
      action: z.string(),
      at: isoDate,
      actor: z.object({ name: z.string().nullable(), email: z.string() }).nullable(),
      meetingId: uuid.nullable(),
    }),
  ),
});
export type KnowledgeDetail = z.infer<typeof knowledgeDetail>;

export const knowledgeQuery = z.object({
  space: uuid.optional(),
  kind: knowledgeKind.optional(),
  /** Only entries with a source in this meeting — the meeting page's "does this change what we know". */
  meeting: uuid.optional(),
  /** Only entries whose sources are meetings a folder holds. */
  folder: uuid.optional(),
});
export type KnowledgeQuery = z.infer<typeof knowledgeQuery>;

export const updateKnowledgeBody = z.object({
  statement: z.string().trim().min(3).max(500).optional(),
  detail: z.string().trim().max(2000).nullable().optional(),
});
export type UpdateKnowledgeBody = z.infer<typeof updateKnowledgeBody>;

export const readEverythingResult = z.object({ queued: z.number().int() });
export type ReadEverythingResult = z.infer<typeof readEverythingResult>;

export const mergeKnowledgeBody = z.object({ into: uuid, others: z.array(uuid).min(1).max(10) });
export type MergeKnowledgeBody = z.infer<typeof mergeKnowledgeBody>;

/** "Different things": never propose these as one again. */
export const distinctKnowledgeBody = z.object({ ids: z.array(uuid).min(2).max(10) });
export type DistinctKnowledgeBody = z.infer<typeof distinctKnowledgeBody>;
